import { useState } from 'react';
import { type Matrix, type CodingCode } from '../../api/coding';
import { ErrorBanner, OkBanner } from '../Layout';
import GenerateMatrixModal from './GenerateMatrixModal';

export const blankMatrix = (): Matrix => ({
  question: '',
  language: 'es',
  instructions: '',
  origin: 'manual',
  normalizer: 'unicode-v1',
  dimensions: [{ name: 'theme', label: 'Tema', max_codes: 1, required: true }],
  codes: [],
  states: [],
});
const blankCode = (): CodingCode => ({
  value: '',
  label: '',
  definition: '',
  dimension: 'theme',
  parent: null,
  inclusions: '',
  exclusions: '',
  examples: [],
  aliases: [],
  patterns: [],
});

export default function MatrixEditor({
  initial,
  initialName = '',
  projectId = 1,
  onSave,
  busy,
}: {
  initial?: Matrix;
  initialName?: string;
  projectId?: number;
  onSave: (name: string, spec: Matrix) => void;
  busy: boolean;
}) {
  const [name, setName] = useState(initialName);
  const [spec, setSpec] = useState<Matrix>(initial ?? blankMatrix());
  const [error, setError] = useState('');
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  const [jsonDraft, setJsonDraft] = useState<string | null>(null);
  const [isIaModalOpen, setIsIaModalOpen] = useState(false);

  function updateCode(index: number, value: Partial<CodingCode>) {
    setSpec({ ...spec, codes: spec.codes.map((c, i) => (i === index ? { ...c, ...value } : c)) });
  }

  async function importFile(file?: File) {
    if (!file) return;
    try {
      const value = JSON.parse(await file.text());
      const matrix = value.specification ?? value;
      if (!Array.isArray(matrix.codes) || !Array.isArray(matrix.dimensions)) {
        throw new Error('El archivo debe contener una matriz de codificación válida con códigos y dimensiones.');
      }
      setSpec(matrix);
      setName(value.name ?? file.name.replace(/\.json$/, ''));
      setError('');
      setSuccessNotice(`Matriz importada desde «${file.name}» (${matrix.codes.length} categorías).`);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(name || spec.question, spec);
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-plano border border-grid rounded-lg">
        <p className="text-xs text-tinta-3 flex-1 min-w-[200px]">
          Defina qué incluye cada categoría. Guardar crea un borrador; publicar fija esta versión para reutilizarla.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn text-xs min-h-[40px] px-3 font-semibold"
            onClick={() => setIsIaModalOpen(true)}
          >
            🪄 Generar categorías con IA
          </button>
          <label className="btn-sec text-xs min-h-[40px] px-3 flex items-center cursor-pointer">
            📁 Importar JSON
            <input
              aria-label="Importar matriz JSON"
              className="sr-only"
              type="file"
              accept=".json"
              onChange={(e) => void importFile(e.target.files?.[0])}
            />
          </label>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}
      {successNotice && <OkBanner message={successNotice} />}

      <GenerateMatrixModal
        isOpen={isIaModalOpen}
        onClose={() => setIsIaModalOpen(false)}
        projectId={projectId}
        initialQuestion={spec.question}
        onMatrixGenerated={(genName, genSpec, usage) => {
          setSpec(genSpec);
          if (genName) setName(genName);
          setJsonDraft(null);
          setError('');
          const usageDetail = usage ? ` · ${usage.model} (${usage.output_tokens} tokens)` : '';
          setSuccessNotice(`Matriz generada con IA exitosamente (${genSpec.codes.length} categorías${usageDetail}). Revise cada código a continuación.`);
        }}
      />

      <label className="etiqueta">
        Nombre de la matriz
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
      </label>
      <label className="etiqueta">
        Pregunta completa
        <textarea
          required
          className="input"
          value={spec.question}
          onChange={(e) => setSpec({ ...spec, question: e.target.value })}
        />
      </label>
    <div className="space-y-2">
      <h3 className="font-semibold">Dimensiones</h3>
      {spec.dimensions.map((d, i) => <div key={i} className="grid grid-cols-1 sm:grid-cols-4 gap-2 border border-grid p-2 rounded">
        <label className="etiqueta">Identificador<input className="input" required value={d.name} onChange={e => setSpec({...spec, dimensions: spec.dimensions.map((x,j) => j === i ? {...x,name:e.target.value} : x)})} /></label>
        <label className="etiqueta">Nombre<input className="input" required value={d.label} onChange={e => setSpec({...spec, dimensions: spec.dimensions.map((x,j) => j === i ? {...x,label:e.target.value} : x)})} /></label>
        <label className="etiqueta">Máximo de códigos<input className="input" type="number" min="1" max="100" value={d.max_codes} onChange={e => setSpec({...spec, dimensions: spec.dimensions.map((x,j) => j === i ? {...x,max_codes:Number(e.target.value)} : x)})} /></label>
        <label className="flex gap-2 items-center text-sm"><input type="checkbox" checked={d.required} onChange={e => setSpec({...spec, dimensions: spec.dimensions.map((x,j) => j === i ? {...x,required:e.target.checked} : x)})} />Obligatoria en respuesta válida</label>
      </div>)}
      <button type="button" className="btn-sec" onClick={() => setSpec({...spec, dimensions: [...spec.dimensions, {name: `dimension${spec.dimensions.length+1}`, label:'Nueva dimensión',max_codes:1,required:false}]})}>Añadir dimensión</button>
    </div>
    <h3 className="font-semibold">Categorías ({spec.codes.length})</h3>
    {spec.codes.length === 0 && <p className="text-sm">Añada al menos una categoría con su definición.</p>}
    {spec.codes.map((c,i) => <fieldset key={i} className="border border-grid rounded p-3 min-w-0 space-y-2">
      <legend className="text-sm">Categoría {i+1}</legend>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <label className="etiqueta">Código estable<input required pattern="[A-Za-z][A-Za-z0-9_]*" className="input" value={c.value} onChange={e => updateCode(i,{value:e.target.value})} /></label>
        <label className="etiqueta">Etiqueta<input required className="input" value={c.label} onChange={e => updateCode(i,{label:e.target.value})} /></label>
        <label className="etiqueta">Dimensión<select className="input" value={c.dimension} onChange={e => updateCode(i,{dimension:e.target.value})}>{spec.dimensions.map(d => <option key={d.name}>{d.name}</option>)}</select></label>
      </div>
      <label className="etiqueta">Definición<textarea required className="input" value={c.definition} onChange={e => updateCode(i,{definition:e.target.value})} /></label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="etiqueta">Qué incluye<textarea className="input" value={c.inclusions} onChange={e => updateCode(i,{inclusions:e.target.value})} /></label>
        <label className="etiqueta">Qué excluye<textarea className="input" value={c.exclusions} onChange={e => updateCode(i,{exclusions:e.target.value})} /></label>
      </div>
      <label className="etiqueta">Respuestas equivalentes exactas (una por línea)<textarea className="input" value={c.aliases.join('\n')} onChange={e => updateCode(i,{aliases:e.target.value.split('\n')})} /></label>
      <details><summary className="cursor-pointer text-sm">Reglas y ejemplos</summary>
        <label className="etiqueta">Patrones de texto (uno por línea)<textarea className="input" value={c.patterns.join('\n')} onChange={e => updateCode(i,{patterns:e.target.value.split('\n').filter(Boolean)})} /></label>
        <label className="etiqueta">Ejemplos sin datos personales<textarea className="input" value={c.examples.join('\n')} onChange={e => updateCode(i,{examples:e.target.value.split('\n').filter(Boolean)})} /></label>
        <label className="etiqueta">Código de categoría superior<input className="input" value={c.parent ?? ''} onChange={e => updateCode(i,{parent:e.target.value || null})} /></label>
      </details>
      <button type="button" className="btn-sec" onClick={() => setSpec({...spec,codes:spec.codes.filter((_,j)=>j!==i)})}>Quitar categoría del borrador</button>
    </fieldset>)}
    <button className="btn-sec" type="button" onClick={() => setSpec({...spec,codes:[...spec.codes,blankCode()]})}>Añadir categoría</button>
    <fieldset className="border border-grid rounded p-3 space-y-2">
      <legend>Estados de respuesta</legend>
      <p className="text-sm text-tinta-3">Escriba equivalencias exactas, una por línea. «No sé, pero falta agua» no se trata como «No sabe». Los campos vacíos se clasifican como «Sin respuesta» automáticamente.</p>
      {Object.entries({NS:'No sabe',NINGUNO:'Ninguno',RECHAZO:'No desea responder',NO_APLICA:'No aplica'}).map(([state,label]) =>
        <label className="etiqueta" key={state}>{label}<textarea className="input" value={spec.states.find(s=>s.state===state)?.aliases.join('\n')??''} onChange={e=>{
          const aliases=e.target.value.split('\n');
          setSpec({...spec,states:[...spec.states.filter(s=>s.state!==state),{state,aliases}]});
        }} /></label>)}
    </fieldset>
    <label className="etiqueta">Criterios adicionales<textarea className="input" value={spec.instructions} onChange={e => setSpec({...spec,instructions:e.target.value})} /></label>
    <details><summary className="cursor-pointer">Avanzado: matriz completa y estados de respuesta</summary>
      <p className="text-sm my-2">Edite el texto y aplíquelo al formulario cuando esté completo. Los cambios sin aplicar no se guardan.</p>
      <textarea aria-label="Matriz JSON" className="input font-mono text-xs h-64" value={jsonDraft ?? JSON.stringify(spec,null,2)} onChange={e => setJsonDraft(e.target.value)} />
      <button type="button" className="btn-sec" disabled={jsonDraft === null} onClick={() => {
        try {
          const parsed = JSON.parse(jsonDraft ?? '');
          if (!Array.isArray(parsed.codes) || !Array.isArray(parsed.dimensions) || !Array.isArray(parsed.states) || typeof parsed.question !== 'string') throw new Error('La matriz requiere pregunta, códigos, dimensiones y estados.');
          setSpec(parsed); setJsonDraft(null); setError('');
        } catch (e) { setError(`No se aplicó el JSON: ${(e as Error).message}`); }
      }}>Aplicar JSON al formulario</button>
      <button type="button" className="btn-sec" disabled={jsonDraft === null} onClick={() => {setJsonDraft(null);setError('');}}>Descartar edición JSON</button>
    </details>
    <button className="btn" disabled={busy || !spec.codes.length || jsonDraft !== null}>{busy ? 'Guardando…' : 'Guardar nueva versión de matriz'}</button>
    </form>
  );
}
